import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { GridLayout } from 'components/grid/GridLayout';
import { ShowHide } from 'components/layout';
import { describeTrigger } from 'pages/employee-reviews/shared';
import {
    getSavedTriggers,
    hasEmployeeDrivenSignOff,
    isTachoInfringementsTopicSelected
} from 'pages/employee-reviews/review-types/shared/reviewTypeEditorHelpers';

const defaultObject = {};

// The lookup data may supply singular or plural unit names, so normalise to the singular
// base before pluralising by the number the unit describes.
function formatUnitName(unitName, count) {
    const baseUnitName = unitName.toLowerCase().replace(/s$/, "");

    if (count === 1) {
        return baseUnitName;
    }

    return `${baseUnitName}s`;
}

export function ReviewTypeSummaryStep(
    {
        reviewType,
        reviewOptions = defaultObject
    }
) {

    function formatStartDate() {
        if (reviewType?.schedule?.startDate == null) {
            return null;
        }

        return moment(reviewType.schedule.startDate).format("DD/MM/YYYY");
    }

    const startDateText = formatStartDate();

    function formatSchedule() {
        if (reviewType?.schedule?.active !== true) {
            return "Not scheduled";
        }

        const unit = (reviewOptions?.frequencyUnits ?? []).find((item) => item.id === reviewType.schedule.frequencyUnitId);

        if (reviewType.schedule.frequency == null || unit == null) {
            return "Scheduled";
        }

        const frequencyText = `Every ${reviewType.schedule.frequency} ${formatUnitName(unit.name, reviewType.schedule.frequency)}`;
        const mode = (reviewOptions?.scheduleModes ?? []).find((item) => item.key === reviewType.schedule.mode);

        if (mode == null) {
            return frequencyText;
        }

        return `${frequencyText} (${mode.name})`;
    }

    function formatAppliesTo() {
        const names = [
            ...(reviewType?.employeeTypes ?? []).map((item) => item.name),
            ...(reviewType?.employeeCategories ?? []).map((item) => item.name),
            ...(reviewType?.employeeStatuses ?? []).map((item) => item.name)
        ];

        return names.join(", ");
    }

    function formatTopics() {
        return (reviewOptions?.topics ?? [])
            .filter((topic) => (reviewType?.topicIds ?? []).some((id) => id === topic.id))
            .map((topic) => topic.name)
            .join(", ");
    }

    function formatIncludeItemsFrom() {
        const includeItemsFrom = reviewType?.tachoTopicSettings?.includeItemsFrom;

        if (includeItemsFrom == null) {
            return "Include infringements from: None";
        }

        return `Include infringements from: ${moment(includeItemsFrom).format("DD/MM/YYYY")}`;
    }

    function formatAllowMarkAllReviewed() {
        const allowed = reviewType?.tachoTopicSettings?.allowMarkAllReviewed === true;

        return `Allow mark all as reviewed: ${allowed ? "On" : "Off"}`;
    }

    function formatAlerts() {
        const alerts = [];

        if (reviewType?.raiseAlertWhenPending === true) {
            alerts.push("Pending review");
        }

        // Gated on the same predicate the save uses, so the summary can never promise an
        // alert the payload drops.
        if (reviewType?.raiseAlertForSignOff === true && hasEmployeeDrivenSignOff(reviewType, reviewOptions)) {
            const delayValue = reviewType.signOffAlertDelayValue ?? 0;
            const delayUnit = (reviewOptions?.advanceNoticeUnits ?? []).find((item) => item.id === reviewType.signOffAlertDelayUnitId);

            if (delayUnit == null) {
                alerts.push("Outstanding sign-off");
            } else {
                alerts.push(`Outstanding sign-off (${delayValue} ${formatUnitName(delayUnit.name, delayValue)} after completion)`);
            }
        }

        if (alerts.length === 0) {
            return null;
        }

        return alerts.join(", ");
    }

    const alertsText = formatAlerts();

    const triggerDescriptions = getSavedTriggers(reviewType).map((trigger) => describeTrigger(trigger, reviewOptions));

    function formatSignOffMethods() {
        return (reviewOptions?.signOffMethods ?? [])
            .filter((method) => (reviewType?.signOffMethodIds ?? []).some((id) => id === method.id))
            .map((method) => method.name)
            .join(", ");
    }

    return (
        <div>
            <h2>Summary</h2>
            <div className="spacer-bottom">
                <GridLayout numberOfColumns={3} gap={20} className="break-word">
                    <div>
                        <h4 className="bold">Review type name</h4>
                        <div>{reviewType?.name}</div>
                    </div>
                    <div>
                        <h4 className="bold">Start date</h4>
                        <div>
                            <ShowHide
                                evaluator={startDateText != null}
                                show={(<span>{startDateText}</span>)}
                                hide={(<em>None</em>)}
                            />
                        </div>
                    </div>
                    <div>
                        <h4 className="bold">Status</h4>
                        <div>{reviewType?.enabled ? "Active" : "Inactive"}</div>
                    </div>
                    <div>
                        <h4 className="bold">Applies To</h4>
                        <div>{formatAppliesTo()}</div>
                    </div>
                    <div>
                        <h4 className="bold">Schedule</h4>
                        <div>{formatSchedule()}</div>
                    </div>
                    <div>
                        <h4 className="bold">Review Topics</h4>
                        <div>{formatTopics()}</div>
                        <ShowHide
                            evaluator={isTachoInfringementsTopicSelected(reviewType, reviewOptions)}
                            show={(
                                <Fragment>
                                    <div>{formatIncludeItemsFrom()}</div>
                                    <div>{formatAllowMarkAllReviewed()}</div>
                                </Fragment>
                            )}
                        />
                    </div>
                    <ShowHide
                        evaluator={triggerDescriptions.length > 0}
                        show={(
                            <div>
                                <h4 className="bold">Triggers</h4>
                                {triggerDescriptions.map((description) => {
                                    return <div key={description}>{description}</div>;
                                })}
                            </div>
                        )}
                    />
                    <div>
                        <h4 className="bold">Sign-off</h4>
                        <div>{reviewType?.requireManagerSignature ? "Manager signature required" : "Manager signature not required"}</div>
                    </div>
                    <div>
                        <h4 className="bold">Sign-off methods</h4>
                        <div>{formatSignOffMethods()}</div>
                    </div>
                    <div>
                        <h4 className="bold">Outcome levels</h4>
                        <div>{reviewType?.outcomeLevelsActive ? "On" : "Off"}</div>
                    </div>
                    <div>
                        <h4 className="bold">Alerts</h4>
                        <div>
                            <ShowHide
                                evaluator={alertsText != null}
                                show={(<span>{alertsText}</span>)}
                                hide={(<span>None</span>)}
                            />
                        </div>
                    </div>
                </GridLayout>
            </div>
        </div>
    );
}

ReviewTypeSummaryStep.propTypes = {
    reviewType: PropTypes.object.isRequired,
    reviewOptions: PropTypes.object
};
